<template>
	<v-row id="params_form" no-gutters class="text-left px-0">
		<v-col cols="12">
			<v-form @submit.prevent="testAndContinue" v-model="formValid" ref="form">
				<v-row no-gutters class="mt-0 mb-3 px-0">
					<v-col cols="12">
						<v-row no-gutters class="mt-0 px-0">
							<v-col cols="12" v-for="param in basicParams" :key="`${editNodeId}_${param.key}`">
								<ParamDispatcherDataProvider
									v-if="!param.hidden"
									:param="param"
									:node="editNode"
									:key="buildFieldKey(param)"
									:disabled="getIsDisabled(param)"
									:workflowVersion="workflowVersion"
									@update-input-field="updateInputField"
									@update-input-field-label="updateInputFieldLabel"
									@update-input-fallback="updateInputFallback"
								/>
							</v-col>
							<SearchOrCreateToggle
								v-if="searchOrCreateAction"
								v-model="searchOrCreateFlag"
								:label="createLabelText"
								:disabled="searchOrCreateToggleDisabled || loadingSearchOrCreateFlag"
							/>

							<v-col
								cols="12"
								class="dynamic-fields-section my-1"
								:class="{ 'is-disabled': loadingExtraFields, 'is-transactional-email': isTransactionalEmails }"
							>
								<v-row no-gutters class="mt-0 px-0">
									<v-col cols="12" v-for="param in dynamicFields" :key="`${editNodeId}_${param.key}`" class="my-1">
										<ParamDispatcherDataProvider
											v-if="!param.hidden"
											:param="param"
											:node="editNode"
											:key="buildFieldKey(param)"
											:disabled="getIsDisabled(param)"
											:workflowVersion="workflowVersion"
											@update-input-field="updateInputField"
											@update-input-field-label="updateInputFieldLabel"
											@update-input-fallback="updateInputFallback"
										/>
									</v-col>
								</v-row>
								<div v-if="loadingExtraFields" class="center-absolutely">
									<v-progress-circular indeterminate color="primary"></v-progress-circular>
								</div>
							</v-col>
							<div v-if="advancedParams.length" class="additional-options-container">
								<div
									@click="additionalOptionsAreOpen = !additionalOptionsAreOpen"
									class="title-container display-flex flex-row flex-align-center"
								>
									<div>Additional options</div>
									<v-icon class="chevron-icon ml-2">{{ `mdi-chevron-${additionalOptionsAreOpen ? 'up' : 'down'}` }}</v-icon>
								</div>
							</div>
							<template v-if="additionalOptionsAreOpen">
								<v-col cols="12" v-for="param in advancedParams" :key="`${editNodeId}_${param.key}`" class="my-1">
									<ParamDispatcherDataProvider
										:param="param"
										:node="editNode"
										:key="buildFieldKey(param)"
										:disabled="getIsDisabled(param)"
										:workflowVersion="workflowVersion"
										@update-input-field="updateInputField"
										@update-input-field-label="updateInputFieldLabel"
										@update-input-fallback="updateInputFallback"
									/>
								</v-col>
							</template>
						</v-row>
					</v-col>
				</v-row>
			</v-form>
		</v-col>
	</v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import ParamDispatcherDataProvider from './ParamDispatcherDataProvider';
import SearchOrCreateToggle from './SearchOrCreateToggle';
import { PARAM_TYPES_MAP } from './block-editor-params.const';
import { editNodeOriginal } from '@/store/modules/selectedNode/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import ParamsService from '@/utils/ParamsService';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'ParamsForm',
	components: {
		ParamDispatcherDataProvider,
		SearchOrCreateToggle,
	},
	props: {},
	computed: {
		...mapGetters('selectedNode', ['editNode', 'editNodeParams', 'editNodeParamsId', 'loadingExtraFields', 'isSearchOrCreate']),
		...mapGetters([
			'actions/getAction',
			'apps/isActionSearchOrCreateOfService',
			'actions/getActionParams',
			editNodeOriginal.fullPath,
			editedWorkflowVersion.fullPath,
		]),
		isTransactionalEmails() {
			// Should change to check action is 'send_email' as well as checking the service ?
			return !!(this.editNode?.service_slug === ServiceSlugs.TransactionalEmails);
		},
		workflowVersion() {
			return this[editedWorkflowVersion.fullPath];
		},
		createLabelText() {
			return `Create a new ${this.actionData.noun} if item not found`;
		},
		editNodeId() {
			return this.editNode.id;
		},
		actionData() {
			return this['actions/getAction'](this.editNode.action);
		},
		params() {
			return this['actions/getActionParams'](this.editNode.action);
		},
		actionOriginalFields() {
			return this.params;
		},
		// all fields including dynamic fields
		allActionFields() {
			if (!this.editNodeParams.length || this.editNodeParamsId !== this.editNode.id) {
				return this.params;
			}
			return this.editNodeParams;
		},
		dynamicFields() {
			// all fields from allActionFields that are not in actionOriginalFields
			return this.allActionFields.filter((field) => field && !this.actionOriginalFields.find((oldField) => oldField.key === field.key));
		},
		searchOrCreateFlag: {
			get() {
				return this.isSearchOrCreate;
			},
			async set() {
				// prevent from user toggling while waiting for server response
				this.loadingSearchOrCreateFlag = true;
				try {
					await this['selectedNode/toggleNodeType'](this.editNode.id);
				} catch (e) {
					//
				}
				this.loadingSearchOrCreateFlag = false;
			},
		},
		searchOrCreateAction() {
			if (this.actionData) {
				return this['apps/isActionSearchOrCreateOfService'](this.editNode.service, this.actionData.id);
			}
			return false;
		},
		editNodeOriginal() {
			return this[editNodeOriginal.fullPath];
		},
		editNodeOriginalParamsMap() {
			return this.editNodeOriginal?.params_map;
		},
		searchOrCreateToggleDisabled() {
			return !WorkflowNodeService.paramsAreProperlyFilledInParamsMap(this.actionOriginalFields, this.editNodeOriginalParamsMap);
		},
		editNodeParamsMap() {
			return this.editNode.params_map;
		},
		basicParams() {
			return this.params.filter((p) => !p.advanced);
		},
		keysToIgnoreWhenCheckingDynamicFields() {
			return this.actionData?.keys_to_ignore_when_clearing_dynamic_fields || [];
		},
		advancedParams() {
			return this.params.filter((p) => p.advanced && !p.hidden);
		},
		advancedParamsKeys() {
			return this.advancedParams.map((param) => param.key);
		},
		shouldShowAdvancedParams() {
			return this.advancedParamsKeys.some((key) => !isNullOrUndefinedOrEmpty(this.editNodeParamsMap[key]));
		},
	},
	watch: {
		dataMenu() {},
		editNodeParams: {
			async handler(newV) {
				if (newV && newV.length && this.editNodeParamsId === this.editNode.id) {
					// keysToIgnoreWhenCheckingDynamicFields make it so that params_map is left with old values.
					const paramsMapKeys = Object.keys(this.editNodeParamsMap).filter(
						(key) => !this.keysToIgnoreWhenCheckingDynamicFields.includes(key)
					);
					// if at least one param in params map is not an option
					const oneOrMoreParamMapKeyMissing = paramsMapKeys.some((paramKey) => newV.findIndex((param) => param.key === paramKey) === -1);
					if (oneOrMoreParamMapKeyMissing && !this.askedClearDynamicFields) {
						// todo, decide when we need to stop asking for this? - for now, we only ask him once
						this.askedClearDynamicFields = true;
						if (confirm('You have some old dynamic fields. Do you want to reset all the dynamic fields?')) {
							await this['selectedNode/resetDynamicFieldsOfEditNode']();
							await this['nodes/getNodeData']({ id: this.editNode.id, forceGet: true });
							this.askedClearDynamicFields = false;
						}
					}
				}
			},
			deep: true,
			immediate: true,
		},
	},
	methods: {
		...mapActions(['selectedNode/toggleNodeType', 'selectedNode/resetDynamicFieldsOfEditNode', 'nodes/getNodeData']),
		testAndContinue() {
			this.$refs.form.validate();
			if (this.formValid) {
				this.$emit('test-and-continue');
			} else {
				alert('Fill all required fields');
			}
		},
		updateInputField(payload) {
			const { map, label, param, preventUpdateEditNode, syncEditNodeAfterUpdate } = payload;
			this.$emit('update-block-param', { ...map, reloadDynamic: param?.dynamic, preventUpdateEditNode, syncEditNodeAfterUpdate });

			// TODO - make sure we dont need this anymore,
			// as this is not used and updateInputfieldLabel is being called directly when needed
			if (label) {
				this.updateInputFieldLabel({ label });
			}
		},
		updateInputFieldLabel(payload) {
			const { label, preventUpdateEditNode } = payload;
			this.$emit('update-block-param-label', { ...label, preventUpdateEditNode });
		},
		updateInputFallback(payload) {
			if (payload) {
				this.$emit('update-params-fallback', payload);
			}
		},
		dataMenuStatus(payload) {
			this.dataMenu = payload.opened;
			if (payload.opened) {
				this.menuOpenForInput = payload.param.key;
			}
		},
		buildFieldKey(param) {
			return ParamsService.buildFieldKey(param, this.editNodeOriginalParamsMap);
		},
		getIsDisabled(param) {
			return ParamsService.getIsDisabled(param, this.editNodeOriginalParamsMap);
		},
		allowParam(param) {
			return !Object.keys(PARAM_TYPES_MAP).includes(param.type);
		},
	},
	data: () => ({
		additionalOptionsAreOpen: false,
		menuOpenForInput: null,
		dataMenu: false,
		showForm: false,
		formValid: false,
		paramsLoaded: {},
		paramsLabelsLoaded: {},
		askedClearDynamicFields: false,
		loadingSearchOrCreateFlag: false,
	}),
	mounted() {
		this.paramsLoaded = {};
		if (this.editNodeParamsMap) {
			Object.keys(this.editNodeParamsMap).forEach((key) => {
				this.paramsLoaded[key] = false;
			});
		}
		// We want to allow the user to hide the `additional options` during step editing but for it to come back if conditions are met.
		this.additionalOptionsAreOpen = this.shouldShowAdvancedParams;
	},
};
</script>

<style scoped lang="scss">
.additional-options-container {
	margin: 5px 0 5px 0;
	.title-container {
		font-size: 14px;
		cursor: pointer;
		background: transparent;
		color: var(--bold-title);

		.chevron-icon {
			width: 7px;
			height: 3px;
			margin-top: 2px;
			color: var(--bold-title);
		}
	}
}

#params_form {
	padding-bottom: 0px;

	.dynamic-fields-section {
		position: relative;
		padding: 0 6px;
		border-radius: 4px;
		background: var(--dialog-bg);
		&.is-transactional-email {
			background: unset;
			position: unset;
			padding: unset;
			border-radius: unset;
		}
	}
}
.param {
	padding-bottom: 0px !important;
}
.param.bot_pad {
	padding-bottom: 450px !important;
}
.tooltip-text {
	color: white !important;
}
</style>
